import React, { useState, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { tableService } from '../services/table';
import { CreateTableRequest } from '../types';
import { getActiveRestaurantId } from '../utils/roleAccess';
import { Users, Search, Plus, Edit, Trash2, UserCheck, Clock, AlertTriangle, X, QrCode, Printer, Download } from 'lucide-react';
import { clsx } from 'clsx';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { ConfirmationModal } from '../components/ConfirmationModal';
import { PageSkeleton, EmptyState, ButtonLoader } from '../components/LoadingStates';
import QRCode from 'react-qr-code';

interface CreateTableForm {
  name: string;
  capacity: number;
}

export const Tables: React.FC = () => {
  const { user } = useAuth();
  const { currentTheme } = useTheme();
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingTable, setEditingTable] = useState<any>(null);
  const [qrTable, setQrTable] = useState<any>(null);
  const qrPrintRef = useRef<HTMLDivElement>(null);
  const [confirmationModal, setConfirmationModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
    type: 'warning' | 'danger' | 'info';
    confirmText: string;
  }>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {},
    type: 'warning',
    confirmText: 'Confirm'
  });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm<CreateTableForm>();

  const activeRestaurantId = getActiveRestaurantId();

  // Fetch tables — filtered by restaurantId when one is active
  const { data: tables, isLoading } = useQuery(
    ['tables', user?.organizationCode, activeRestaurantId, statusFilter],
    () => tableService.getTables({
      org: user?.organizationCode,
      ...(activeRestaurantId && { restaurantId: activeRestaurantId }),
      ...(statusFilter && { status: statusFilter as any })
    }),
    { enabled: !!user?.organizationCode }
  );

  // Create table mutation
  const createTableMutation = useMutation(
    (data: CreateTableForm) => tableService.createTable({
      name: data.name,
      capacity: data.capacity,
      organizationCode: user?.organizationCode!,
      restaurantId: activeRestaurantId!
    }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['tables']);
        toast.success('Table created successfully!');
        setShowCreateModal(false);
        setEditingTable(null);
        reset();
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.message || 'Failed to create table');
      }
    }
  );

  // Update table mutation
  const updateTableMutation = useMutation(
    ({ id, data }: { id: string; data: Partial<CreateTableRequest> }) =>
      tableService.updateTable(id, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['tables']);
        toast.success('Table updated successfully!');
        setShowCreateModal(false);
        setEditingTable(null);
        reset();
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.message || 'Failed to update table');
      }
    }
  );

  // Delete table mutation
  const deleteTableMutation = useMutation(tableService.deleteTable, {
    onSuccess: () => {
      queryClient.invalidateQueries(['tables']);
      toast.success('Table deleted successfully!');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to delete table');
    }
  });

  // Toggle table status mutation
  const toggleStatusMutation = useMutation(
    ({ id, active }: { id: string; active: boolean }) =>
      tableService.toggleTableStatus(id, active),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['tables']);
        toast.success('Table status updated successfully!');
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.message || 'Failed to update table status');
      }
    }
  );

  const onSubmit = (data: CreateTableForm) => {
    if (!editingTable && !activeRestaurantId) {
      toast.error('Please select a restaurant before adding a table.');
      return;
    }
    if (editingTable) {
      updateTableMutation.mutate({
        id: editingTable.id,
        data: {
          name: data.name,
          capacity: data.capacity,
          organizationCode: user?.organizationCode!
        }
      });
    } else {
      createTableMutation.mutate(data);
    }
  };

  const handleEdit = (table: any) => {
    setEditingTable(table);
    reset({
      name: table.name,
      capacity: table.capacity
    });
    setShowCreateModal(true);
  };

  const handleDelete = (table: any) => {
    setConfirmationModal({
      isOpen: true,
      title: 'Delete Table',
      message: `Are you sure you want to delete "${table.name}"? This action cannot be undone.`,
      onConfirm: () => {
        deleteTableMutation.mutate(table.id);
        setConfirmationModal(prev => ({ ...prev, isOpen: false }));
      },
      type: 'danger',
      confirmText: 'Delete'
    });
  };

  const handleToggleStatus = (table: any) => {
    const action = table.active ? 'deactivate' : 'activate';
    setConfirmationModal({
      isOpen: true,
      title: `${action.charAt(0).toUpperCase() + action.slice(1)} Table`,
      message: `Are you sure you want to ${action} "${table.name}"?`,
      onConfirm: () => {
        toggleStatusMutation.mutate({
          id: table.id,
          active: !table.active
        });
        setConfirmationModal(prev => ({ ...prev, isOpen: false }));
      },
      type: table.active ? 'warning' : 'info',
      confirmText: action.charAt(0).toUpperCase() + action.slice(1)
    });
  };

  const getQrUrl = (table: any) => {
    const restaurantId = activeRestaurantId || table.restaurantId;
    if (!restaurantId) return null;
    return `${window.location.origin}/table-orders/${restaurantId}/${table.id}`;
  };

  const handleDownloadQr = () => {
    const svgEl = qrPrintRef.current?.querySelector('svg');
    if (!svgEl) return;
    const svgData = new XMLSerializer().serializeToString(svgEl);
    const blob = new Blob([svgData], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `qr-${qrTable?.name?.replace(/\s+/g, '-') ?? 'table'}.svg`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handlePrintQr = () => {
    if (!qrPrintRef.current) return;
    const printContents = qrPrintRef.current.innerHTML;
    const win = window.open('', '_blank', 'width=400,height=500');
    if (!win) return;
    win.document.write(`
      <html><head><title>QR Code - ${qrTable?.name}</title>
      <style>body{font-family:sans-serif;text-align:center;padding:30px;}h2{margin:0 0 8px;}p{color:#666;margin:4px 0;font-size:14px;}</style>
      </head><body>${printContents}</body></html>
    `);
    win.document.close();
    win.focus();
    win.print();
    win.close();
  };

  const filteredTables = tables?.filter(table =>
    table.name.toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'AVAILABLE': return <UserCheck className="h-5 w-5" />;
      case 'IN_USE': return <Users className="h-5 w-5" />;
      case 'RESERVED': return <Clock className="h-5 w-5" />;
      case 'OUT_OF_SERVICE': return <AlertTriangle className="h-5 w-5" />;
      default: return <Users className="h-5 w-5" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'AVAILABLE': return 'bg-green-100 text-green-800 border-green-200';
      case 'IN_USE': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'RESERVED': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'OUT_OF_SERVICE': return 'bg-red-100 text-red-800 border-red-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const statusOptions = [
    { value: '', label: 'All Status' },
    { value: 'AVAILABLE', label: 'Available' },
    { value: 'IN_USE', label: 'In Use' },
    { value: 'RESERVED', label: 'Reserved' },
    { value: 'OUT_OF_SERVICE', label: 'Out of Service' }
  ];

  if (isLoading) {
    return <PageSkeleton type="grid" />;
  }

  const availableCount = tables?.filter(t => t.status === 'AVAILABLE').length || 0;
  const inUseCount = tables?.filter(t => t.status === 'IN_USE').length || 0;
  const reservedCount = tables?.filter(t => t.status === 'RESERVED').length || 0;
  const outOfServiceCount = tables?.filter(t => t.status === 'OUT_OF_SERVICE').length || 0;

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
        <div className="mb-4 sm:mb-0">
          <h1 className="text-2xl font-bold flex items-center" style={{ color: currentTheme.colors.text.primary }}>
            <Users className="h-6 w-6 mr-2" style={{ color: currentTheme.colors.primary[600] }} />
            Table Management
          </h1>
          <p style={{ color: currentTheme.colors.text.secondary }}>Manage your restaurant tables and seating</p>
        </div>
        <button
          onClick={() => {
            setEditingTable(null);
            reset({ name: '', capacity: 0 });
            setShowCreateModal(true);
          }}
          className="inline-flex items-center px-4 py-2 text-white rounded-lg transition-colors"
          style={{
            backgroundColor: currentTheme.colors.primary[600],
          }}
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = currentTheme.colors.primary[700]}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = currentTheme.colors.primary[600]}
        >
          <Plus className="h-5 w-5 mr-2" />
          Add Table
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center">
            <UserCheck className="h-8 w-8 text-green-600 mr-3" />
            <div>
              <p className="text-2xl font-bold text-green-900">{availableCount}</p>
              <p className="text-sm text-green-600">Available</p>
            </div>
          </div>
        </div>
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center">
            <Users className="h-8 w-8 text-blue-600 mr-3" />
            <div>
              <p className="text-2xl font-bold text-blue-900">{inUseCount}</p>
              <p className="text-sm text-blue-600">In Use</p>
            </div>
          </div>
        </div>
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-center">
            <Clock className="h-8 w-8 text-yellow-600 mr-3" />
            <div>
              <p className="text-2xl font-bold text-yellow-900">{reservedCount}</p>
              <p className="text-sm text-yellow-600">Reserved</p>
            </div>
          </div>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <AlertTriangle className="h-8 w-8 text-red-600 mr-3" />
            <div>
              <p className="text-2xl font-bold text-red-900">{outOfServiceCount}</p>
              <p className="text-sm text-red-600">Out of Service</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div 
        className="rounded-lg border p-4 mb-6"
        style={{ 
          backgroundColor: currentTheme.colors.background.card,
          borderColor: currentTheme.colors.border.primary 
        }}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search tables..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:border-2"
              style={{ 
                borderColor: currentTheme.colors.border.primary,
                backgroundColor: currentTheme.colors.background.secondary,
                color: currentTheme.colors.text.primary,
                outline: 'none'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = currentTheme.colors.primary[500];
                e.target.style.boxShadow = `0 0 0 2px ${currentTheme.colors.primary[100]}`;
              }}
              onBlur={(e) => {
                e.target.style.borderColor = currentTheme.colors.border.primary;
                e.target.style.boxShadow = 'none';
              }}
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 border rounded-lg focus:ring-2"
            style={{ 
              borderColor: currentTheme.colors.border.primary,
              backgroundColor: currentTheme.colors.background.secondary,
              color: currentTheme.colors.text.primary,
              outline: 'none'
            }}
            onFocus={(e) => {
              e.target.style.borderColor = currentTheme.colors.primary[500];
              e.target.style.boxShadow = `0 0 0 2px ${currentTheme.colors.primary[100]}`;
            }}
            onBlur={(e) => {
              e.target.style.borderColor = currentTheme.colors.border.primary;
              e.target.style.boxShadow = 'none';
            }}
          >
            {statusOptions.map(option => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Tables Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredTables.map((table) => (
          <div
            key={table.id}
            className="rounded-xl border shadow-sm card-hover p-6 transition-all bg-white"
            style={{ borderColor: currentTheme.colors.border.primary }}
          >
            {/* Header with Table Name and Status */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <div className="p-2 rounded-lg mr-3" style={{ backgroundColor: currentTheme.colors.primary[100] }}>
                  {getStatusIcon(table.status)}
                </div>
                <div>
                  <h3 className="text-lg font-semibold" style={{ color: currentTheme.colors.text.primary }}>
                    {table.name}
                  </h3>
                  <p className="text-sm" style={{ color: currentTheme.colors.text.secondary }}>
                    {table.capacity} seats capacity
                  </p>
                </div>
              </div>
              <span className={clsx(
                'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium',
                table.active
                  ? 'bg-green-100 text-green-800'
                  : 'bg-gray-100 text-gray-800'
              )}>
                {table.active ? 'Active' : 'Inactive'}
              </span>
            </div>

            {/* Status Info */}
            <div className="mb-4">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium" style={{ color: currentTheme.colors.text.secondary }}>
                  Current Status:
                </span>
                <span className={clsx(
                  'inline-flex items-center px-3 py-1 rounded-full text-sm font-medium',
                  getStatusColor(table.status)
                )}>
                  {getStatusIcon(table.status)}
                  <span className="ml-2">{table.status.replace(/_/g, ' ')}</span>
                </span>
              </div>
            </div>
            
            {/* Action Buttons */}
            <div className="flex items-center justify-between pt-4 border-t" style={{ borderColor: currentTheme.colors.border.primary }}>
              <button 
                onClick={() => handleToggleStatus(table)}
                className="px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                style={{ 
                  backgroundColor: table.active ? '#fef2f2' : '#f0fdf4',
                  color: table.active ? '#dc2626' : '#16a34a',
                  border: `1px solid ${table.active ? '#fecaca' : '#bbf7d0'}`
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = table.active ? '#fee2e2' : '#dcfce7';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = table.active ? '#fef2f2' : '#f0fdf4';
                }}
                title={table.active ? 'Click to make table inactive' : 'Click to make table active'}
              >
                {table.active ? 'Make Inactive' : 'Make Active'}
              </button>
              <div className="flex items-center space-x-2">
                {getQrUrl(table) && (
                  <button
                    onClick={() => setQrTable(table)}
                    className="p-2 rounded-lg transition-colors"
                    style={{ color: '#7c3aed', backgroundColor: '#f5f3ff' }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#ede9fe'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#f5f3ff'}
                    title="Show / Print QR Code"
                  >
                    <QrCode className="h-4 w-4" />
                  </button>
                )}
                <button
                  onClick={() => handleEdit(table)}
                  className="p-2 rounded-lg transition-colors"
                  style={{
                    color: currentTheme.colors.primary[600],
                    backgroundColor: currentTheme.colors.primary[50]
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = currentTheme.colors.primary[100]}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = currentTheme.colors.primary[50]}
                  title="Edit Table"
                >
                  <Edit className="h-4 w-4" />
                </button>
                <button
                  onClick={() => handleDelete(table)}
                  className="p-2 rounded-lg transition-colors bg-red-50"
                  style={{ color: '#dc2626' }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#fef2f2'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#fef2f2'}
                  title="Delete Table"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {filteredTables.length === 0 && (
        <EmptyState
          icon={<Users className="h-16 w-16" style={{ color: currentTheme.colors.text.secondary }} />}
          title="No Tables Found"
          description={searchTerm ? 'Try adjusting your search or filters.' : 'Get started by adding your first table.'}
          action={
            !searchTerm ? (
              <button
                onClick={() => {
                  setEditingTable(null);
                  reset();
                  setShowCreateModal(true);
                }}
                className="inline-flex items-center px-4 py-2 text-white rounded-lg transition-colors"
                style={{ backgroundColor: currentTheme.colors.primary[600] }}
              >
                <Plus className="h-5 w-5 mr-2" />
                Add Your First Table
              </button>
            ) : undefined
          }
        />
      )}

      {/* Create Table Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div 
            className="rounded-xl max-w-md w-full p-6"
            style={{ backgroundColor: currentTheme.colors.background.card }}
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold" style={{ color: currentTheme.colors.text.primary }}>
                {editingTable ? 'Edit Table' : 'Add New Table'}
              </h2>
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setEditingTable(null);
                  reset();
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Table Name
                </label>
                <input
                  {...register('name', {
                    required: 'Table name is required',
                    minLength: {
                      value: 1,
                      message: 'Table name must be at least 1 character'
                    }
                  })}
                  type="text"
                  placeholder="e.g., Table 1, VIP Table A"
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2"
                  style={{ 
                    borderColor: currentTheme.colors.border.primary,
                    backgroundColor: currentTheme.colors.background.secondary,
                    color: currentTheme.colors.text.primary,
                    outline: 'none'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = currentTheme.colors.primary[500];
                    e.target.style.boxShadow = `0 0 0 2px ${currentTheme.colors.primary[100]}`;
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = currentTheme.colors.border.primary;
                    e.target.style.boxShadow = 'none';
                  }}
                />
                {errors.name && (
                  <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Seating Capacity
                </label>
                <input
                  {...register('capacity', {
                    required: 'Capacity is required',
                    min: {
                      value: 1,
                      message: 'Capacity must be at least 1'
                    },
                    max: {
                      value: 20,
                      message: 'Capacity cannot exceed 20'
                    },
                    valueAsNumber: true
                  })}
                  type="number"
                  placeholder="e.g., 4"
                  min="1"
                  max="20"
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2"
                  style={{ 
                    borderColor: currentTheme.colors.border.primary,
                    backgroundColor: currentTheme.colors.background.secondary,
                    color: currentTheme.colors.text.primary,
                    outline: 'none'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = currentTheme.colors.primary[500];
                    e.target.style.boxShadow = `0 0 0 2px ${currentTheme.colors.primary[100]}`;
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = currentTheme.colors.border.primary;
                    e.target.style.boxShadow = 'none';
                  }}
                />
                {errors.capacity && (
                  <p className="mt-1 text-sm text-red-600">{errors.capacity.message}</p>
                )}
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false);
                    setEditingTable(null);
                    reset();
                  }}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={createTableMutation.isLoading || updateTableMutation.isLoading}
                  className="px-4 py-2 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                  style={{ backgroundColor: currentTheme.colors.primary[600] }}
                  onMouseEnter={(e) => !e.currentTarget.disabled && (e.currentTarget.style.backgroundColor = currentTheme.colors.primary[700])}
                  onMouseLeave={(e) => !e.currentTarget.disabled && (e.currentTarget.style.backgroundColor = currentTheme.colors.primary[600])}
                >
                  {(createTableMutation.isLoading || updateTableMutation.isLoading) ? (
                    <ButtonLoader text={editingTable ? 'Updating...' : 'Creating...'} />
                  ) : (
                    editingTable ? 'Update Table' : 'Create Table'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* QR Code Modal */}
      {qrTable && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl max-w-xs w-full p-6 shadow-2xl text-center">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                <QrCode className="h-5 w-5 text-purple-600" />
                {qrTable.name}
              </h2>
              <button onClick={() => setQrTable(null)} className="text-gray-400 hover:text-gray-600">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div ref={qrPrintRef} className="flex flex-col items-center">
              <h2 style={{ margin: '0 0 8px', fontSize: 18 }}>{qrTable.name}</h2>
              <p style={{ color: '#666', marginBottom: 16, fontSize: 13 }}>
                Scan to view menu &amp; place order
              </p>
              <div className="p-3 bg-white border-2 border-gray-100 rounded-xl inline-block">
                <QRCode
                  value={getQrUrl(qrTable)!}
                  size={200}
                  style={{ display: 'block' }}
                />
              </div>
              <p style={{ color: '#999', fontSize: 11, marginTop: 10, wordBreak: 'break-all', maxWidth: 220 }}>
                {getQrUrl(qrTable)}
              </p>
            </div>

            <div className="flex gap-2 mt-5">
              <button
                onClick={() => setQrTable(null)}
                className="flex-1 py-2 rounded-xl text-gray-700 bg-gray-100 text-sm font-medium hover:bg-gray-200"
              >
                Close
              </button>
              <button
                onClick={handleDownloadQr}
                className="flex-1 py-2 rounded-xl text-white text-sm font-semibold flex items-center justify-center gap-2"
                style={{ backgroundColor: '#0891b2' }}
              >
                <Download className="h-4 w-4" /> Download
              </button>
              <button
                onClick={handlePrintQr}
                className="flex-1 py-2 rounded-xl text-white text-sm font-semibold flex items-center justify-center gap-2"
                style={{ backgroundColor: '#7c3aed' }}
              >
                <Printer className="h-4 w-4" /> Print
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={confirmationModal.isOpen}
        onClose={() => setConfirmationModal(prev => ({ ...prev, isOpen: false }))}
        onConfirm={confirmationModal.onConfirm}
        title={confirmationModal.title}
        message={confirmationModal.message}
        confirmText={confirmationModal.confirmText}
        type={confirmationModal.type}
        isLoading={deleteTableMutation.isLoading || toggleStatusMutation.isLoading}
      />
    </div>
  );
};